import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { isEmpty, map } from 'es-toolkit/compat';
import React, { useState } from 'react';

import { ModelUserPreferences } from 'utils/models';
import {
  getDefaultSpotDetectionSetup,
  getSpotDetectionCompartmentOptions,
  getSpotDetectionDilationChipLabel,
  getSpotDetectionSelectedGeometryLabel,
  MAX_SPOT_DETECTION_DILATION_UM,
  normalizeSpotDetectionCompartments,
  normalizeSpotDetectionDilationsUm,
} from '../../../spotDetectionInputSetup';
import { HScoreBinsEditor } from './HScoreBinsEditor';
import { PerProbeCountBinsOverrides, ProbeCountBinsSetup } from './PerProbeCountBinsOverrides';

interface SpotDetectionInputSetupProps {
  availableGeometries?: string[];
  setup?: Pick<
    ModelUserPreferences,
    'segmentationCompartments' | 'segmentationDilationsUm' | 'countBins' | 'positivityThresholdScore'
  >;
  onChange: (
    setup: Pick<
      ModelUserPreferences,
      'segmentationCompartments' | 'segmentationDilationsUm' | 'countBins' | 'positivityThresholdScore'
    >
  ) => void;
  // Every probe uses the general bins above unless it has its own entry here.
  probeNames?: string[];
  probeDisplayNames?: Record<string, string>;
  perProbeSetup?: Record<string, ProbeCountBinsSetup>;
  onProbeSetupChange?: (probeName: string, setup: ProbeCountBinsSetup | undefined) => void;
  showPositivityThreshold?: boolean;
  showHScoreBins?: boolean;
  singleDilation?: boolean;
  showSummary?: boolean;
}

const parseOptionalInteger = (value: string): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }
  const parsedValue = Number(value);
  if (!Number.isInteger(parsedValue) || parsedValue < 0) {
    return undefined;
  }
  return parsedValue;
};

export const SpotDetectionInputSetup: React.FC<SpotDetectionInputSetupProps> = ({
  setup,
  availableGeometries,
  onChange,
  probeNames = [],
  probeDisplayNames = {},
  perProbeSetup,
  onProbeSetupChange,
  showPositivityThreshold = true,
  showHScoreBins = true,
  singleDilation = false,
  showSummary = true,
}) => {
  const compartmentOptions = getSpotDetectionCompartmentOptions(availableGeometries);
  const currentSetup = {
    ...getDefaultSpotDetectionSetup(),
    ...setup,
    segmentationCompartments: normalizeSpotDetectionCompartments(setup?.segmentationCompartments, availableGeometries),
    segmentationDilationsUm: setup?.segmentationDilationsUm ?? getDefaultSpotDetectionSetup().segmentationDilationsUm,
    countBins: setup?.countBins ?? getDefaultSpotDetectionSetup().countBins,
  };
  const selectedGeometryLabel = getSpotDetectionSelectedGeometryLabel(
    currentSetup.segmentationCompartments,
    availableGeometries
  );
  const hasSelectedCompartment = (currentSetup.segmentationCompartments || []).length > 0;
  const [dilationDraft, setDilationDraft] = useState('');
  const validSingleDilation =
    currentSetup.segmentationDilationsUm.length === 1 &&
    Number.isInteger(currentSetup.segmentationDilationsUm[0]) &&
    currentSetup.segmentationDilationsUm[0] >= 0 &&
    currentSetup.segmentationDilationsUm[0] <= MAX_SPOT_DETECTION_DILATION_UM;

  const updateSetup = (
    nextSetup: Partial<
      Pick<
        ModelUserPreferences,
        'segmentationCompartments' | 'segmentationDilationsUm' | 'countBins' | 'positivityThresholdScore'
      >
    >
  ) => {
    onChange({
      segmentationCompartments: nextSetup.segmentationCompartments ?? currentSetup.segmentationCompartments,
      segmentationDilationsUm: nextSetup.segmentationDilationsUm ?? currentSetup.segmentationDilationsUm,
      countBins: nextSetup.countBins ?? currentSetup.countBins,
      positivityThresholdScore: nextSetup.positivityThresholdScore ?? currentSetup.positivityThresholdScore,
    });
  };

  const addDilation = () => {
    const parsedDilation = parseOptionalInteger(dilationDraft);
    if (parsedDilation === undefined) {
      return;
    }
    updateSetup({
      segmentationDilationsUm: normalizeSpotDetectionDilationsUm([
        ...(currentSetup.segmentationDilationsUm || []),
        parsedDilation,
      ]),
    });
    setDilationDraft('');
  };

  const geometrySubtitle = 'Cell compartment';
  const dilationSubtitle = 'Compartment expansion';
  // Distinct from the separate mean-intensity/OD H-Score action elsewhere in this form.
  const countBinsSubtitle = 'Instance H-score bins';
  const settingSubtitleSx = { fontWeight: 700 };

  return (
    <Grid container pt={2} direction="column" spacing={1.5} flexWrap="nowrap" sx={{ maxWidth: 720 }}>
      <Grid item>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={settingSubtitleSx}>
              {geometrySubtitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" display="block">
              Count spots inside this compartment. Default: Nuclear.
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={currentSetup.segmentationCompartments}
              onChange={(_event, nextCompartments: string[]) => {
                if (isEmpty(nextCompartments)) {
                  return;
                }
                updateSetup({
                  segmentationCompartments: normalizeSpotDetectionCompartments(nextCompartments, availableGeometries),
                });
              }}
              aria-label={geometrySubtitle}
              sx={{ mt: 1, flexWrap: 'wrap' }}
            >
              {map(compartmentOptions, (option) => (
                <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {!hasSelectedCompartment && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Select at least one geometry before adding dilations.
              </Alert>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={settingSubtitleSx}>
              {dilationSubtitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" display="block">
              Expand {selectedGeometryLabel.toLowerCase()} before counting. Default: 2 µm; allowed: 0–
              {MAX_SPOT_DETECTION_DILATION_UM} µm.
            </Typography>
            <Grid container spacing={1} alignItems="center" mt={1}>
              <Grid item xs>
                <FormControl fullWidth>
                  <TextField
                    size="small"
                    type="number"
                    label="Dilation (µm)"
                    inputProps={{ min: 0, max: MAX_SPOT_DETECTION_DILATION_UM, step: 1 }}
                    value={
                      singleDilation
                        ? currentSetup.segmentationDilationsUm.length === 1
                          ? currentSetup.segmentationDilationsUm[0]
                          : ''
                        : dilationDraft
                    }
                    error={singleDilation && !validSingleDilation}
                    helperText={
                      singleDilation && !validSingleDilation
                        ? `Enter one whole-number dilation from 0 to ${MAX_SPOT_DETECTION_DILATION_UM} µm.`
                        : undefined
                    }
                    onChange={(event) =>
                      singleDilation
                        ? updateSetup({
                            segmentationDilationsUm:
                              event.target.value.trim() === '' ? [] : [Number(event.target.value)],
                          })
                        : setDilationDraft(event.target.value)
                    }
                  />
                </FormControl>
              </Grid>
              {!singleDilation && (
                <Grid item>
                  <Button size="small" startIcon={<AddIcon />} onClick={addDilation} disabled={!hasSelectedCompartment}>
                    Add dilation
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>

          {showHScoreBins && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={settingSubtitleSx}>
                {countBinsSubtitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" display="block">
                Assign every spot count to one score. Ranges remain continuous.
              </Typography>
              <HScoreBinsEditor
                showPositivityThreshold={showPositivityThreshold}
                countBins={currentSetup.countBins || []}
                onChange={(countBins) => updateSetup({ countBins })}
                positivityThresholdScore={currentSetup.positivityThresholdScore}
                onPositivityThresholdScoreChange={(positivityThresholdScore) =>
                  updateSetup({ positivityThresholdScore })
                }
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      {showHScoreBins && onProbeSetupChange && (
        <Grid item>
          <PerProbeCountBinsOverrides
            showPositivityThreshold={showPositivityThreshold}
            probeNames={probeNames}
            probeDisplayNames={probeDisplayNames}
            generalCountBins={currentSetup.countBins || []}
            generalPositivityThresholdScore={currentSetup.positivityThresholdScore}
            perProbeSetup={perProbeSetup}
            onProbeSetupChange={onProbeSetupChange}
          />
        </Grid>
      )}

      {showSummary && (
        <>
          <Grid item>
            <Divider />
          </Grid>
          <Grid item>
            <Typography variant="subtitle1" fontWeight={700}>
              Summary
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={settingSubtitleSx} display="block">
                    {geometrySubtitle}
                  </Typography>
                  <Typography variant="body2">{selectedGeometryLabel}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={settingSubtitleSx} display="block">
                    {dilationSubtitle}
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" mt={0.5}>
                    {map(currentSetup.segmentationDilationsUm, (dilation) => (
                      <Chip
                        key={dilation}
                        size="small"
                        label={getSpotDetectionDilationChipLabel(
                          dilation,
                          currentSetup.segmentationCompartments,
                          availableGeometries
                        )}
                        onDelete={() =>
                          updateSetup({
                            segmentationDilationsUm: (currentSetup.segmentationDilationsUm || []).filter(
                              (value) => value !== dilation
                            ),
                          })
                        }
                      />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </>
      )}
    </Grid>
  );
};
